import type { GridType, RowFormat } from 'common/types/scalarSpreadsheet';

// Mirrors backend CellAnnotationType (apps/cell_annotations/models/cell_annotation.py).
// 'cross_reference' renders today (cap-table fully-diluted-total + FOD net-shares);
// 'pre_fx_currency' wire shape is supported but the popout rendering wires up in
// Task #7. Keep the union aligned with the backend enum so the hydrator's
// `as CellAnnotationType` cast is honest at runtime.
export type CellAnnotationType = 'cross_reference' | 'pre_fx_currency';

export type CellAnnotationRowMode = 'single_cell' | 'single_row' | 'multi_row';

export interface CellAnnotationColumnConfig {
  alias: string;
  header?: string;
  // First column (index 0) inherits gridType / format / currency from the host
  // cell; fields here apply to metadata columns (index >= 1).
  gridType?: GridType;
  format?: RowFormat;
}

export interface CellAnnotationConfig {
  // Which annotation slot this cell binds to. Today only 'cross_reference'
  // is rendered; the field exists so the cellKey, the wire payload, and the
  // popup all agree on the slot a config targets.
  annotationType: CellAnnotationType;
  rowMode: CellAnnotationRowMode;
  columns: CellAnnotationColumnConfig[];
}

export interface CellAnnotationRowState {
  position: number;
  value: number | null;
  metadata: Record<string, unknown>;
  isPlaceholder?: boolean;
}

export interface CellAnnotationState {
  rowMode: CellAnnotationRowMode;
  columnsConfig: CellAnnotationColumnConfig[];
  rows: CellAnnotationRowState[];
  isDirty: boolean;
}

export interface CellAnnotationWireRow {
  position: number;
  value: string | null;
  metadata: Record<string, unknown>;
}

export interface CellAnnotationWireEntry {
  row_mode: CellAnnotationRowMode;
  columns_config: CellAnnotationColumnConfig[];
  rows: CellAnnotationWireRow[];
}

// Lets a host show a pre-FX source amount with a different sign than the one it is saved with.
export interface PreFxAmountSign {
  toDisplay: (stored: number) => number;
  toStored: (displayed: number) => number;
}

// Per-parent nested wire shape: field_alias -> annotation_type -> entry.
// Matches the backend serializer output and is the shape consumers send back
// over the wire on save.
export type CellAnnotationWireMap = Record<string, Partial<Record<CellAnnotationType, CellAnnotationWireEntry>>>;

// CellKey segments: parentType:parentId:fieldAlias:annotationType
// The annotation-type segment lets one cell carry multiple annotation kinds
// (e.g. cross_reference today, pre_fx_currency in a later task) without the
// store collisions that would happen if two configs claimed the same slot.
export type CellAnnotationCellKey = string;

export interface ParsedCellAnnotationCellKey {
  parentType: string;
  parentId: string;
  fieldAlias: string;
  annotationType: CellAnnotationType;
}

export const buildCellKey = (
  parentType: string,
  parentId: number | string,
  fieldAlias: string,
  annotationType: CellAnnotationType
): CellAnnotationCellKey => `${parentType}:${parentId}:${fieldAlias}:${annotationType}`;

// Parse a colon-joined cellKey into its four segments. Returns null when the
// key is malformed (e.g. legacy 3-segment shape) so callers can skip rather
// than route entries to the wrong slot.
export const parseCellKey = (cellKey: string): ParsedCellAnnotationCellKey | null => {
  const parts = cellKey.split(':');
  if (parts.length !== 4) {
    return null;
  }
  const [parentType, parentId, fieldAlias, annotationType] = parts;
  return {
    parentType,
    parentId,
    fieldAlias,
    annotationType: annotationType as CellAnnotationType,
  };
};
